import {
  FlowHumanTaskBlockWorkContextContentType,
  type FlowHumanTaskWorkContext,
  FlowHumanTaskWorkContextPortal,
  FlowProducedType,
} from 'flow-schema';
import * as uuid from 'uuid';

import { assertNever } from 'common';

export const humanTaskWorkContextContentTypeNames: Record<
  FlowHumanTaskBlockWorkContextContentType,
  string
> = {
  [FlowHumanTaskBlockWorkContextContentType.Ehr]: 'EHR',
  [FlowHumanTaskBlockWorkContextContentType.Url]: 'URL',
  [FlowHumanTaskBlockWorkContextContentType.Pdf]: 'PDF',
  [FlowHumanTaskBlockWorkContextContentType.Markdown]: 'Markdown Content',
  [FlowHumanTaskBlockWorkContextContentType.File]: 'File',
  [FlowHumanTaskBlockWorkContextContentType.Portal]: 'Portal',
  [FlowHumanTaskBlockWorkContextContentType.VoiceCall]: '[Legacy] Conversational Voice v1',
  [FlowHumanTaskBlockWorkContextContentType.VoiceCallV2]: 'Conversational Voice',
};

/**
 * Portals staff can link to. Partial by design: flow-api accepts every member of
 * `FlowHumanTaskWorkContextPortal`, but a portal is only selectable in the builder, and only
 * openable from a task, once someone supplies its name and URL.
 */
export const humanTaskWorkContextContentTypePortalUrls: Partial<
  Record<FlowHumanTaskWorkContextPortal, { name: string; portalUrl: string }>
> = {
  [FlowHumanTaskWorkContextPortal.Availity]: {
    name: 'Availity',
    portalUrl: 'https://apps.availity.com/web/onboarding/availity-fr-ui/#/login',
  },
  [FlowHumanTaskWorkContextPortal.Bcbsri]: {
    name: 'BCBSRI',
    portalUrl: 'https://www.bcbsri.com/providers/',
  },
  [FlowHumanTaskWorkContextPortal.Carelon]: {
    name: 'Carelon',
    portalUrl: 'https://www.providerportal.com/',
  },
  [FlowHumanTaskWorkContextPortal.CareCore]: {
    name: 'CareCore',
    portalUrl: 'https://www.evicore.com/',
  },
  [FlowHumanTaskWorkContextPortal.Cohere]: {
    name: 'Cohere',
    portalUrl: 'https://login.coherehealth.com/',
  },
  [FlowHumanTaskWorkContextPortal.MedSolutions]: {
    name: 'MedSolutions',
    portalUrl: 'https://myevicoreportal.medsolutions.com/',
  },
  [FlowHumanTaskWorkContextPortal.RadMd]: {
    name: 'RadMD',
    portalUrl:
      (import.meta.env.VITE_RADMD_PORTAL_URL as string | undefined) ||
      'https://www.radmd.com/RadMD/Common/UserMenu.aspx',
  },
  [FlowHumanTaskWorkContextPortal.Regal]: {
    name: 'Regal',
    portalUrl: 'https://regalmed.okta.com/',
  },
  [FlowHumanTaskWorkContextPortal.UnitedHealthcare]: {
    name: 'United Healthcare',
    portalUrl: 'https://www.uhcprovider.com/',
  },
};

export function getDefaultHumanTaskWorkContextConfig(
  contentType: FlowHumanTaskBlockWorkContextContentType
): FlowHumanTaskWorkContext {
  const id = uuid.v4();
  switch (contentType) {
    case FlowHumanTaskBlockWorkContextContentType.Ehr:
      return {
        id,
        contentType,
        ehrInstance: '',
      };
    case FlowHumanTaskBlockWorkContextContentType.Url:
      return {
        id,
        contentType,
        url: '',
      };
    case FlowHumanTaskBlockWorkContextContentType.Pdf:
      return {
        id,
        contentType,
        documentId: '',
      };
    case FlowHumanTaskBlockWorkContextContentType.Markdown:
      return {
        id,
        contentType,
        markdownContent: '',
      };
    case FlowHumanTaskBlockWorkContextContentType.File:
      return {
        id,
        contentType,
        fileReference: '',
      };
    case FlowHumanTaskBlockWorkContextContentType.Portal:
      return {
        id,
        contentType,
        portal: FlowHumanTaskWorkContextPortal.RadMd,
      };
    case FlowHumanTaskBlockWorkContextContentType.VoiceCall:
      return {
        id,
        contentType,
        voiceCall: {
          sourceBlockName: '',
          consumesType: FlowProducedType.VoiceCall,
        },
        callSummary: '',
      };
    case FlowHumanTaskBlockWorkContextContentType.VoiceCallV2:
      return {
        id,
        contentType,
        voiceCallId: '',
      };
    default:
      assertNever('Unknown field type', contentType);
  }
}
